// src/pages/misc/landing/HeroPreviews.tsx
// Hero product previews — miniaturized Dashboard / Leads / Workspace from real app UI + preview data.
//
// [HANDOFF] WHAT THE LIVE LANDING ACTUALLY USES FROM THIS FILE: only `LandingHeroPair` (the last export, bottom of file). Everything
//   else (HERO_PREVIEWS, DECK, HeroPreviewStack, the HERO_* constants) is for the lab page LandingCardsLabPage.tsx and can be left out of a
//   minimal swap, BUT LandingHeroPair needs `WorkspacePreview` and `PipelinePreview` (and WorkspacePreview needs MiniDial etc.), so keep the file whole.
// [SPACING] [ANIMATION] The hero pair is a fixed 644 x 590 design that is scaled to fit its container (CSS: .vl-hero-slot / .vl-hero-pair in
//   landing.css, `transform: scale(100cqw / 644px)`). Two 460 x 310 cards: the Workspace glass card on top-left (0,0, z-index 3) and the
//   Pipeline card behind it at left 184px, top 280px (z-index 1). Nothing here moves; the cards are static.
// [VERIFY] The numbers, names and phones in these previews are fake sample data on purpose (no backend). Card look classes come from
//   src/index.css (exp-landing-black-glass, exp-section-card, exp-landing-progressive-glass); the `data-water-cast` attribute lets the
//   animated background reflect on the glass (read by components/dashboard/ClassicLensBackground).
// [DEPENDS-ON] components/leads/LeadInitialsBadge.tsx and components/leads/LeadStageBadge.tsx (new files outside this folder).
import React, { useEffect, useRef, useState } from 'react';
import LeadInitialsBadge from '../../../components/leads/LeadInitialsBadge';
import { stagePillStyle } from '../../../components/leads/LeadStageBadge';
import MetricsPreview from './MetricsPreview';
import PipelinePreview from './PipelinePreview';

const STAGES = [
  { name: 'Discovery', color: '#3b82f6', pct: 100, count: 386 },
  { name: 'Qualified', color: '#8b5cf6', pct: 62, count: 240 },
  { name: 'Presentation', color: '#6366f1', pct: 41, count: 158 },
  { name: 'Negotiation', color: '#f59e0b', pct: 28, count: 108 },
  { name: 'Closed Won', color: '#EAB308', pct: 12, count: 47 },
] as const;

const STAGE_COLOR: Record<string, string> = {
  Discovery: '#3b82f6',
  Qualified: '#8b5cf6',
  Presentation: '#6366f1',
  Negotiation: '#f59e0b',
  'Closed Won': '#EAB308',
};

const PREVIEW_LEADS = [
  { name: 'Carlos Mendez', stage: 'Qualified', phone: '+52 984 123 4567', fill: 0.7 },
  { name: 'Ana Gutierrez', stage: 'Discovery', phone: '+52 984 555 0198', fill: 0.45 },
  { name: 'Luis Hernandez', stage: 'Presentation', phone: '+1 305 441 2201', fill: 0.85 },
] as const;

const MiniDial: React.FC<{ fill?: number; size?: number; id: string }> = ({
  fill = 0.55,
  size = 42,
  id,
}) => {
  const VB = 64;
  const c = 32;
  const r = 24;
  const stroke = 8;
  const ARC_START = -150;
  const ARC_SWEEP = 300;
  const a0 = ARC_START;
  const a1 = ARC_START + ARC_SWEEP;
  const fillTo = ARC_START + Math.max(0.08, Math.min(1, fill)) * ARC_SWEEP;

  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: c + r * Math.sin(rad), y: c - r * Math.cos(rad) };
  };
  const arc = (from: number, to: number) => {
    const s = polar(from);
    const e = polar(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  return (
    <svg viewBox={`0 0 ${VB} ${VB}`} width={size} height={size} className="overflow-visible shrink-0" aria-hidden>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={c - r} y1={c} x2={c + r} y2={c}>
          <stop offset="0%" stopColor="#BFF3FF" />
          <stop offset="100%" stopColor="#7AE4FF" />
        </linearGradient>
      </defs>
      <path d={arc(a0, a1)} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth={stroke} strokeLinecap="round" />
      <path
        d={arc(a0, fillTo)}
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        style={{ filter: 'drop-shadow(0 0 6px rgba(122,228,255,0.65))' }}
      />
    </svg>
  );
};

const DashboardPreview: React.FC = () => (
  <div className="px-6 py-5 h-full flex flex-col gap-4">
    <div className="flex items-center justify-between">
        <p className="text-sm uppercase tracking-[0.2em] font-semibold text-white/55">Stages</p>
      <span className="text-sm text-white/45 font-medium">Ocean View Residences</span>
    </div>

    <div className="space-y-3 flex-1">
      {STAGES.map((stage) => (
        <div key={stage.name} className="flex items-center gap-3">
          <span className="w-[7.25rem] text-right text-sm text-white/80 font-medium truncate shrink-0">
            {stage.name}
          </span>
          <div className="flex-1 h-9 rounded-full bg-white/[0.08] overflow-hidden">
            <div
              className="h-full rounded-full flex items-center justify-end pr-3 min-w-[3.25rem]"
              style={{
                width: `${Math.max(stage.pct, 16)}%`,
                background: `linear-gradient(90deg, ${stage.color}bb 0%, ${stage.color} 50%, ${stage.color} 100%)`,
                boxShadow: `0 0 16px ${stage.color}66`,
              }}
            >
              <span className="text-xs font-bold text-white">{stage.pct}%</span>
            </div>
          </div>
          <span className="w-10 text-right text-sm text-white/60 font-semibold tabular-nums">{stage.count}</span>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-3 gap-3 pt-1">
      {[
        { n: '26%', label: 'Cierre' },
        { n: '$368', label: 'Costo / lead' },
        { n: '312', label: 'Llamadas' },
      ].map((k) => (
        <div key={k.label} className="rounded-xl bg-white/[0.08] border border-white/10 px-3 py-3 text-center">
          <p className="text-white text-xl font-bold leading-none tracking-tight">{k.n}</p>
          <p className="text-white/50 text-xs mt-1.5 leading-tight">{k.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const MiniStagePill: React.FC<{ name: string; color: string; order: number }> = ({ name, color, order }) => (
  <span
    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-medium whitespace-nowrap"
    style={stagePillStyle(color, name)}
  >
    <span className="font-light tabular-nums text-white/90">{order}</span>
    {name}
  </span>
);

const LeadsPreview: React.FC = () => (
  <div className="px-5 py-5 h-full flex flex-col gap-3.5">
    <div className="flex items-center justify-between px-1">
      <p className="text-sm uppercase tracking-[0.2em] font-semibold px-0" style={{ color: '#4b5563' }}>Leads</p>
      <span className="text-sm font-medium" style={{ color: '#6b7280' }}>Lista</span>
    </div>

    <div className="flex-1 flex flex-col justify-center gap-3">
      {PREVIEW_LEADS.map((lead, i) => (
        <div key={lead.name} className="flex items-center gap-2.5">
          <MiniDial id={`hero-ld-${i}`} fill={lead.fill} size={42} />
          <div
            className="flex-1 min-w-0 rounded-[1.35rem] px-4 py-3 flex items-center gap-3"
            style={{
              background: 'rgba(255,255,255,0.94)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
            }}
          >
            <LeadInitialsBadge
              name={lead.name}
              stage={lead.stage}
              stageColor={STAGE_COLOR[lead.stage]}
              size={30}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-base truncate leading-tight" style={{ color: '#111827' }}>
                {lead.name}
              </p>
              <p className="text-sm truncate mt-0.5" style={{ color: '#1d4ed8' }}>
                {lead.phone}
              </p>
            </div>
            <MiniStagePill
              name={lead.stage}
              color={STAGE_COLOR[lead.stage]}
              order={STAGES.findIndex((s) => s.name === lead.stage) + 1}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const WorkspacePreview: React.FC = () => (
  <div className="h-full flex flex-col overflow-hidden">
    {/* Faded header + dial, like the real workspace */}
    <div
      className="shrink-0 px-4 py-3 flex items-center gap-3"
      style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.22) 70%, transparent 100%)',
      }}
    >
      <LeadInitialsBadge name="Carlos Mendez" stage="Qualified" stageColor="#8b5cf6" size={34} />
      <div className="min-w-0 flex-1">
        <p className="text-white text-base font-semibold truncate leading-tight">Carlos Mendez</p>
        <p className="text-white/60 text-xs truncate">WhatsApp · Sarah Chen</p>
      </div>
      <MiniDial id="hero-ws-dial" fill={0.7} size={40} />
    </div>

    {/* Inbound → AI reply → call last */}
    <div className="flex-1 px-4 pb-4 pt-1 flex flex-col justify-end gap-2.5 overflow-hidden">
      <div
        className="self-start max-w-[86%] rounded-2xl rounded-tl-md px-3.5 py-2.5 shadow-md"
        style={{ background: '#ffffff' }}
      >
        <p className="text-[13px] leading-snug" style={{ color: '#1f2937' }}>
          Hola, vi <span className="font-bold">Aldea Zamá</span> en Facebook. ¿Sigue disponible?
        </p>
      </div>

      <div
        className="self-end max-w-[86%] rounded-2xl rounded-tr-md bg-gray-800 px-3.5 py-2.5 border border-blue-400/80"
        style={{
          boxShadow:
            '0 0 0 1px rgba(125, 211, 252, 0.45), 0 0 18px rgba(56, 189, 248, 0.85), 0 0 36px rgba(37, 99, 235, 0.55)',
        }}
      >
        <p className="text-[13px] text-white leading-snug">
          ¡Hola, Carlos! Sí, en <span className="font-bold">Aldea Zamá</span> aún hay departamentos de 2 recámaras. ¿Te mando detalles?
        </p>
        <p className="text-[11px] text-sky-300 font-semibold mt-1">Sent with AI</p>
      </div>

      <div className="self-center w-[68%] rounded-2xl border border-gray-700/50 bg-gray-800/50 backdrop-blur-md px-3 py-2.5 flex items-center gap-2.5 shadow-lg">
        <div className="w-8 h-8 rounded-full bg-gray-700/80 flex items-center justify-center shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#d1d5db" aria-hidden>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.35a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.54.57 2.35.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">Phone Call</p>
          <p className="text-gray-300 text-[11px]">Duration 8:24</p>
          <p className="text-[11px] text-blue-300 font-medium">View transcript</p>
        </div>
      </div>
    </div>
  </div>
);

type PreviewKey = 'dashboard' | 'leads' | 'workspace';

const CARD_W = 460;
const CARD_H = 310;
const STEP_Y = Math.round(CARD_H * 0.75); // ~25% vertical overlap
const STEP_X = Math.round(CARD_W * 0.4); // ~60% horizontal overlap
const CYCLE_MS = 4600;

const SHIFT_X = 16;

export const HERO_CARD_W = CARD_W;
export const HERO_CARD_H = CARD_H;
export const HERO_STACK_STEP_X = STEP_X;
export const HERO_STACK_SHIFT_X = SHIFT_X;

export const HERO_PREVIEWS: {
  key: PreviewKey;
  label: string;
  node: React.ReactNode;
  top: number;
  left: number;
  rotate: number;
  shell: string;
}[] = [
  { key: 'dashboard', label: 'Dashboard', node: <DashboardPreview />, top: 0, left: SHIFT_X, rotate: -1.6, shell: 'exp-landing-black-glass' },
  { key: 'leads', label: 'Leads', node: <LeadsPreview />, top: STEP_Y, left: SHIFT_X + Math.round(STEP_X / 2), rotate: 0.6, shell: 'exp-section-card' },
  { key: 'workspace', label: 'Workspace', node: <WorkspacePreview />, top: STEP_Y * 2, left: SHIFT_X + STEP_X, rotate: 1.6, shell: 'exp-landing-progressive-glass' },
];

/** Landing deck. Lab page still uses the original three HERO_PREVIEWS. */
const DECK: {
  key: string;
  label: string;
  node: React.ReactNode;
  shell: string;
  /** 0 = white behind the card. 100 = the real background shows through. */
  background: number;
}[] = [
  { key: 'dashboard', label: 'Stages', node: <DashboardPreview />, shell: 'exp-landing-black-glass', background: 50 },
  { key: 'leads', label: 'Leads', node: <LeadsPreview />, shell: 'exp-section-card', background: 0 },
  { key: 'workspace', label: 'Workspace', node: <WorkspacePreview />, shell: 'exp-landing-progressive-glass', background: 100 },
  { key: 'pipeline', label: 'Pipeline', node: <PipelinePreview />, shell: 'exp-section-card', background: 0 },
  { key: 'metrics', label: 'Métricas', node: <MetricsPreview />, shell: 'exp-landing-black-glass', background: 50 },
];

// [ANIMATION] LAB ONLY (HeroPreviewStack is not on the live landing). A 5-card deck that fans 3 cards (each 52px right / 118px down
//   from the previous). Every 4.6s the front card changes: cards glide to their new slot over 1.6s, the card that arrives "pops"
//   (grows 28 x 18px with a white + cyan glow over 0.52s), and a card leaving fades out over 1.1s after a 0.2s delay.
const VISIBLE = 3;
const FAN_X = 52;
const FAN_Y = 118;
const MOVE_MS = 1600;
const FADE_DELAY = 200;
const GROW_W = 28;
const GROW_H = 18;

const slotFor = (depth: number) => {
  const place = Math.min(depth, VISIBLE - 1);
  return { top: place * FAN_Y, left: place * FAN_X };
};

export const HeroPreviewStack: React.FC = () => {
  const [front, setFront] = useState(3);
  const [settled, setSettled] = useState(3);
  const frontRef = useRef(3);
  const timers = useRef<number[]>([]);

  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  };

  const goTo = (next: number) => {
    const current = frontRef.current;
    if (current === next) return;
    frontRef.current = next;
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
    setSettled(-1);
    setFront(next);
    later(() => setSettled(next), MOVE_MS);
  };

  useEffect(() => {
    const id = window.setInterval(() => {
      goTo((frontRef.current + 1) % DECK.length);
    }, CYCLE_MS);
    return () => {
      window.clearInterval(id);
      timers.current.forEach((timerId) => window.clearTimeout(timerId));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- goTo is recreated every render; the interval is set once on mount and cleared on unmount, never re-created (WU-147 D2 iv, the exhaustive-deps warning naming 'goTo')
  }, []);

  const last = slotFor(VISIBLE - 1);

  return (
    <div
      className="landing-hero-stack relative mx-auto"
      style={{ width: CARD_W + last.left, height: CARD_H + last.top + 12, maxWidth: '100%' }}
    >
      <style>{`
        .landing-hero-stack .landing-hero-card {
          position: absolute !important;
          border-radius: 2rem;
          overflow: hidden;
        }
      `}</style>
      {DECK.map((card, i) => {
        const depth = (i - front + DECK.length) % DECK.length;
        const visible = depth < VISIBLE;
        const slot = slotFor(depth);
        const arrived = i === settled;
        const z = 40 - depth * 8;
        const pop = arrived ? 1 : 0;
        const fadingOut = !visible;

        return (
          <div
            key={card.key}
            aria-hidden={!visible}
            className="absolute text-left pointer-events-none"
            style={{
              top: slot.top,
              left: slot.left,
              width: CARD_W,
              height: CARD_H,
              zIndex: z,
              opacity: visible ? 1 : 0,
              transition: `top ${MOVE_MS}ms ease, left ${MOVE_MS}ms ease, opacity ${fadingOut ? 1100 : 600}ms ease ${fadingOut ? FADE_DELAY : 0}ms`,
            }}
          >
            <div
              className="absolute"
              style={{
                top: arrived ? -(GROW_H / 2) : 0,
                left: arrived ? -(GROW_W / 2) : 0,
                width: arrived ? CARD_W + GROW_W : CARD_W,
                height: arrived ? CARD_H + GROW_H : CARD_H,
                transition: 'top 520ms ease, left 520ms ease, width 520ms ease, height 520ms ease',
              }}
            >
              {card.background < 100 && (
                <div
                  className="pointer-events-none absolute inset-0 rounded-[2rem] bg-white"
                  style={{ opacity: 1 - card.background / 100 }}
                  aria-hidden
                />
              )}
              <div
                className={`landing-hero-card ${card.shell} absolute inset-0`}
                style={
                  {
                    transform: 'none',
                    ['--exp-pop' as string]: pop,
                    transition: 'box-shadow 520ms ease',
                    boxShadow: `0 0 ${26 * pop}px rgba(255,255,255,${0.5 * pop}), 0 0 ${64 * pop}px rgba(122,228,255,${0.4 * pop})`,
                  } as React.CSSProperties
                }
                data-water-cast=""
              >
                <div className="relative z-[1] h-full overflow-hidden rounded-[2rem]">{card.node}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// [HANDOFF] THIS is the export the landing page imports (LandingPage.tsx, hero beat). See the header note about scaling.
/**
 * Hero pair at the real landing-card size (460×310).
 * Pipeline sits behind; the workspace glass sits on top so the board shows through.
 */
export const LandingHeroPair: React.FC = () => (
  <div className="vl-hero-slot">
    <div className="vl-hero-pair">
      <div className="vl-hero-card vl-hero-pipeline exp-section-card" data-water-cast="">
        <div className="relative z-[1] h-full overflow-hidden rounded-[2rem]">
          <PipelinePreview maxPerColumn={4} />
        </div>
      </div>
      <div className="vl-hero-card vl-hero-workspace exp-landing-progressive-glass" data-water-cast="">
        <div className="relative z-[1] h-full overflow-hidden rounded-[2rem]">
          <WorkspacePreview />
        </div>
      </div>
    </div>
  </div>
);

export default HeroPreviewStack;
